import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import brandLogo from "./brand-logo.png";
import Icon from "../home/Icon";
import { NAV, NAV_ACTIONS, isExternal, splitHref } from "./navData";
import useNavTo from "./useNavTo";
import "./UnAuthHeader.css";

// Four top-level groups (Product, Solutions, Pricing, Resources) and three
// actions. The menu itself lives in navData.js; this file only renders it.

const DESKTOP_QUERY = "(min-width: 1100px)";
const HOVER_QUERY = "(min-width: 1100px) and (hover: hover)";
const CLOSE_DELAY = 140;

const matches = (query) =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;

const Wordmark = () => (
  <span className="rl-wordmark">
    <img src={brandLogo} alt="Raya" className="rl-logo" width="80" height="30" />
    <span className="rl-wordmark-erp">ERP</span>
  </span>
);

const linksOf = (item) =>
  item.href ? [item.href] : (item.columns || []).flatMap((c) => c.items.map((i) => i.href));

// The top-level item the current page belongs to (first match wins). The home
// page itself belongs to nobody; a home anchor counts only when it is the hash.
const CLAIMED = NAV.flatMap((item) => item.activeFor || []);

const activeIdFor = ({ pathname, hash }) => {
  const owner = NAV.find((item) => (item.activeFor || []).includes(pathname));
  if (owner) return owner.id;
  if (CLAIMED.includes(pathname)) return null;
  const hit = NAV.find((item) =>
    linksOf(item).some((href) => {
      if (isExternal(href)) return false;
      const { path, hash: h } = splitHref(href);
      if (path !== pathname) return false;
      return h ? `#${h}` === hash : pathname !== "/";
    })
  );
  return hit ? hit.id : null;
};

/* ─── One link inside a panel ───────────────────────────────────────────── */

const PanelLink = ({ item, col, onFollow }) => (
  <a
    href={item.href}
    className="rl-panel-link"
    data-nav-link=""
    data-col={col}
    onClick={(e) => onFollow(item.href, e)}
  >
    <span className="rl-panel-icon"><Icon name={item.icon} size={17} /></span>
    <span className="rl-panel-text">
      <span className="rl-panel-label">
        {item.label}
        {item.badge && <span className="rl-panel-badge">{item.badge}</span>}
      </span>
      {item.desc && <span className="rl-panel-desc">{item.desc}</span>}
    </span>
  </a>
);

/* ─── Header ─────────────────────────────────────────────────────────────── */

export const UnAuthHeader = () => {
  const [openId, setOpenId] = useState(null);       // desktop panel
  const [sheetOpen, setSheetOpen] = useState(false); // mobile sheet
  const [sheetGroup, setSheetGroup] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef(null);
  const triggerRefs = useRef({});
  const panelRefs = useRef({});
  const closeTimer = useRef(null);
  const location = useLocation();
  const navTo = useNavTo();
  const activeId = activeIdFor(location);

  const clearCloseTimer = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const closeAll = useCallback(() => { clearCloseTimer(); setOpenId(null); setSheetOpen(false); }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Any navigation closes whatever is open.
  useEffect(() => { closeAll(); }, [location.pathname, location.hash, closeAll]);

  // Outside click and Esc.
  useEffect(() => {
    if (!openId && !sheetOpen) return undefined;
    const onDown = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) closeAll(); };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const returnTo = openId && triggerRefs.current[openId];
      closeAll();
      if (returnTo) returnTo.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [openId, sheetOpen, closeAll]);

  // Lock page scroll behind the mobile sheet; drop the sheet if the window grows.
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onResize = () => { if (matches(DESKTOP_QUERY)) setSheetOpen(false); };
    window.addEventListener("resize", onResize);
    return () => { document.body.style.overflow = prev; window.removeEventListener("resize", onResize); };
  }, [sheetOpen]);

  useEffect(() => () => clearCloseTimer(), []);

  const follow = (href, e) => { closeAll(); navTo(href, e); };

  /* Desktop panel focus management */
  const panelLinks = (id) => Array.from(panelRefs.current[id]?.querySelectorAll("[data-nav-link]") || []);
  const focusFirst = (id) => { const l = panelLinks(id); if (l[0]) l[0].focus(); };

  const onTriggerKeyDown = (id) => (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpenId(id);
      // The panel renders on the next frame.
      requestAnimationFrame(() => focusFirst(id));
    }
  };

  const onPanelKeyDown = (id) => (e) => {
    const links = panelLinks(id);
    const i = links.indexOf(document.activeElement);
    if (i < 0) return;
    const go = (n) => { e.preventDefault(); links[(n + links.length) % links.length].focus(); };
    const cur = links[i];
    const col = Number(cur.dataset.col);
    switch (e.key) {
      case "ArrowDown": go(i + 1); break;
      case "ArrowUp": go(i - 1); break;
      case "Home": go(0); break;
      case "End": go(links.length - 1); break;
      case "ArrowRight":
      case "ArrowLeft": {
        const dir = e.key === "ArrowRight" ? 1 : -1;
        const cols = [...new Set(links.map((l) => Number(l.dataset.col)))];
        const nextCol = cols[(cols.indexOf(col) + dir + cols.length) % cols.length];
        const rowInCol = links.filter((l) => Number(l.dataset.col) === col).indexOf(cur);
        const target = links.filter((l) => Number(l.dataset.col) === nextCol);
        if (target.length) { e.preventDefault(); target[Math.min(rowInCol, target.length - 1)].focus(); }
        break;
      }
      default:
    }
  };

  const onItemEnter = (id) => () => {
    if (!matches(HOVER_QUERY)) return;
    clearCloseTimer();
    setOpenId(id);
  };
  const onItemLeave = (id) => () => {
    if (!matches(HOVER_QUERY)) return;
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpenId((cur) => (cur === id ? null : cur)), CLOSE_DELAY);
  };
  // Tabbing out of an open group closes it.
  const onItemBlur = (id) => (e) => {
    const li = e.currentTarget;
    if (!li.contains(e.relatedTarget)) setOpenId((cur) => (cur === id ? null : cur));
  };

  return (
    <header ref={headerRef} className={`rl-header ${scrolled ? "is-scrolled" : ""} ${openId ? "has-panel" : ""}`}>
      <div className="rl-header-inner">
        <a href="/" className="rl-header-brand" onClick={(e) => follow("/", e)} aria-label="Raya ERP home">
          <Wordmark />
        </a>

        {/* ── Desktop ─────────────────────────────── */}
        <nav className="rl-nav" aria-label="Main">
          <ul className="rl-nav-list">
            {NAV.map((item) => {
              const isActive = activeId === item.id;
              if (item.kind === "link") {
                return (
                  <li key={item.id} className="rl-nav-item">
                    <a
                      href={item.href}
                      className={`rl-nav-top ${isActive ? "is-active" : ""}`}
                      aria-current={isActive ? "page" : undefined}
                      onClick={(e) => follow(item.href, e)}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              }
              const open = openId === item.id;
              const panelId = `rl-panel-${item.id}`;
              return (
                <li
                  key={item.id}
                  className={`rl-nav-item rl-nav-item--${item.kind}`}
                  onMouseEnter={onItemEnter(item.id)}
                  onMouseLeave={onItemLeave(item.id)}
                  onBlur={onItemBlur(item.id)}
                >
                  <button
                    type="button"
                    ref={(el) => { triggerRefs.current[item.id] = el; }}
                    className={`rl-nav-top ${isActive ? "is-active" : ""} ${open ? "is-open" : ""}`}
                    aria-expanded={open}
                    aria-controls={panelId}
                    onClick={(e) => {
                      clearCloseTimer();
                      // With a mouse on a hover device the panel is already open
                      // from mouseenter, so a click keeps it open rather than
                      // toggling it shut. Keyboard activation (detail 0) toggles.
                      if (e.detail !== 0 && matches(HOVER_QUERY)) setOpenId(item.id);
                      else setOpenId(open ? null : item.id);
                    }}
                    onKeyDown={onTriggerKeyDown(item.id)}
                  >
                    {item.label}
                    <Icon name="chevron-down" size={15} className="rl-nav-chevron" />
                  </button>
                  <div
                    id={panelId}
                    ref={(el) => { panelRefs.current[item.id] = el; }}
                    className={`rl-panel rl-panel--${item.kind}`}
                    hidden={!open}
                    onKeyDown={onPanelKeyDown(item.id)}
                  >
                    <div className="rl-panel-inner">
                      <div className={`rl-panel-cols rl-panel-cols--${item.id}`}>
                        {item.columns.map((c, ci) => (
                          <div className={`rl-panel-col ${c.wide ? "is-wide" : ""}`} key={c.title}>
                            <div className="rl-panel-title">{c.title}</div>
                            <div className="rl-panel-links">
                              {c.items.map((it) => (
                                <PanelLink key={it.label} item={it} col={ci} onFollow={follow} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {item.footer && (
                      <div className="rl-panel-footer">
                        <div className="rl-panel-footer-inner">
                          {item.footer.map((f) => (
                            <a key={f.label} href={f.href} className="rl-panel-footlink" data-nav-link="" data-col={item.columns.length} onClick={(e) => follow(f.href, e)}>
                              <Icon name={f.icon} size={14} />{f.label}<Icon name="arrow-right" size={13} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="rl-header-actions">
          <a href={NAV_ACTIONS.login.href} className="rl-header-login" onClick={(e) => follow(NAV_ACTIONS.login.href, e)}>{NAV_ACTIONS.login.label}</a>
          <a href={NAV_ACTIONS.demo.href} className="rl-header-demo" onClick={(e) => follow(NAV_ACTIONS.demo.href, e)}>{NAV_ACTIONS.demo.label}</a>
          <a href={NAV_ACTIONS.primary.href} className="rl-header-cta" onClick={(e) => follow(NAV_ACTIONS.primary.href, e)}>{NAV_ACTIONS.primary.label}</a>
        </div>

        {/* ── Compact (< 1100px) ─────────────────────────────── */}
        <div className="rl-header-compact">
          <a href={NAV_ACTIONS.primary.href} className="rl-header-cta rl-header-cta--compact" onClick={(e) => follow(NAV_ACTIONS.primary.href, e)}>{NAV_ACTIONS.primary.label}</a>
          <button
            type="button"
            className={`rl-header-toggle ${sheetOpen ? "is-open" : ""}`}
            onClick={() => { setOpenId(null); setSheetOpen(!sheetOpen); }}
            aria-label={sheetOpen ? "Close menu" : "Open menu"}
            aria-expanded={sheetOpen}
            aria-controls="rl-sheet"
          >
            <span className="rl-header-bar" />
            <span className="rl-header-bar" />
            <span className="rl-header-bar" />
          </button>
        </div>
      </div>

      {/* Desktop panel backdrop: dims the page while a mega menu is open. */}
      {openId && <div className="rl-panel-scrim" aria-hidden="true" onMouseDown={closeAll} />}

      <div id="rl-sheet" className="rl-sheet" hidden={!sheetOpen}>
        <nav className="rl-sheet-body" aria-label="Main">
          {NAV.map((item) => {
            if (item.kind === "link") {
              return (
                <a key={item.id} href={item.href} className={`rl-sheet-top ${activeId === item.id ? "is-active" : ""}`} onClick={(e) => follow(item.href, e)}>
                  {item.label}
                </a>
              );
            }
            const expanded = sheetGroup === item.id;
            const groupId = `rl-sheet-${item.id}`;
            return (
              <div key={item.id} className={`rl-sheet-group ${expanded ? "is-open" : ""}`}>
                <button
                  type="button"
                  className={`rl-sheet-top ${activeId === item.id ? "is-active" : ""}`}
                  aria-expanded={expanded}
                  aria-controls={groupId}
                  onClick={() => setSheetGroup(expanded ? null : item.id)}
                >
                  {item.label}
                  <Icon name="chevron-down" size={18} className="rl-nav-chevron" />
                </button>
                <div id={groupId} className="rl-sheet-panel" hidden={!expanded}>
                  {item.columns.map((c) => (
                    <div className="rl-sheet-col" key={c.title}>
                      <div className="rl-panel-title">{c.title}</div>
                      {c.items.map((it) => (
                        <a key={it.label} href={it.href} className="rl-sheet-link" onClick={(e) => follow(it.href, e)}>
                          <span className="rl-panel-icon"><Icon name={it.icon} size={16} /></span>
                          <span className="rl-panel-label">
                            {it.label}
                            {it.badge && <span className="rl-panel-badge">{it.badge}</span>}
                          </span>
                        </a>
                      ))}
                    </div>
                  ))}
                  {item.footer && item.footer.map((f) => (
                    <a key={f.label} href={f.href} className="rl-sheet-foot" onClick={(e) => follow(f.href, e)}>
                      {f.label} <Icon name="arrow-right" size={13} />
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="rl-sheet-actions">
          <a href={NAV_ACTIONS.primary.href} className="rl-header-cta" onClick={(e) => follow(NAV_ACTIONS.primary.href, e)}>{NAV_ACTIONS.primary.label}</a>
          <a href={NAV_ACTIONS.demo.href} className="rl-header-demo" onClick={(e) => follow(NAV_ACTIONS.demo.href, e)}>{NAV_ACTIONS.demo.label}</a>
          <a href={NAV_ACTIONS.login.href} className="rl-header-login" onClick={(e) => follow(NAV_ACTIONS.login.href, e)}>{NAV_ACTIONS.login.label}</a>
        </div>
      </div>
    </header>
  );
};
